import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import { useProjectContext } from './ProjectContext';
import {
  defineFragment,
  deleteFragment,
  getFragment,
  listFragments,
} from '@/api/fragments.api';
import {
  PIPELINE_TYPE,
  type DefineFragmentRequest,
  type FragmentDetail,
  type FragmentListItem,
  type UserFragmentDetail,
} from '@/interfaces/fragments.interface';
import { getApiErrorMessage } from '@/api/base.api';

interface FragmentsContextType {
  fragments: FragmentListItem[];
  isEditorModalOpen: boolean;
  setIsEditorModalOpen: (open: boolean) => void;
  /** Fragment being edited; null = the editor creates a new fragment. */
  currentEditingFragment: UserFragmentDetail | null;
  setCurrentEditingFragment: (fragment: UserFragmentDetail | null) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isLoading: boolean;
  error: string | null;
  hasLoadedFragments: boolean;
  /** Upsert (define is create + edit); re-throws so the modal can display. */
  saveFragment: (body: DefineFragmentRequest) => Promise<void>;
  /** Delete by name; re-throws so callers can react. */
  removeFragment: (name: string) => Promise<void>;
  /** Fetch the full FragmentDef (user or governed); re-throws on failure. */
  fetchFragmentDetail: (name: string) => Promise<FragmentDetail>;
  filteredFragments: FragmentListItem[];
  refetchFragments: () => Promise<void>;
}

const FragmentsContext = createContext<FragmentsContextType | undefined>(
  undefined
);

interface FragmentsProviderProps {
  children: ReactNode;
}

export const FragmentsProvider: React.FC<FragmentsProviderProps> = ({
  children,
}) => {
  // Get current project ID from ProjectContext
  const { selectedProjectId } = useProjectContext();

  // State
  const [fragments, setFragments] = useState<FragmentListItem[]>([]);
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  const [currentEditingFragment, setCurrentEditingFragment] =
    useState<UserFragmentDetail | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if fragments have been loaded at least once
  const [hasLoadedFragments, setHasLoadedFragments] = useState(false);

  // A project switch clears the list, but a request already in flight for the
  // previous project still resolves. Without a guard its answer lands on the
  // new project: project A's fragments reappear under B, and a delayed delete
  // filters a same-named fragment out of B's list. Every operation records
  // the project it was issued for and a generation, and writes state only
  // while both are still the current ones.
  //
  // Written during render rather than in the effect below: an operation
  // started between a project-changing render and that effect would otherwise
  // compare against the previous project and drop its own valid result.
  const currentProjectRef = useRef(selectedProjectId);
  currentProjectRef.current = selectedProjectId;
  const requestGenerationRef = useRef(0);

  /**
   * Opens an operation.
   *
   * Two guards, because the two hazards are not the same. A project change
   * invalidates every operation: its answer describes a project the user is
   * no longer looking at. A LATER operation only invalidates an earlier one
   * that would write the whole list -- two list loads racing must not both
   * commit, so the last one issued wins.
   *
   * A delete writes one row, not the list, so it passes
   * `supersedesLoads: false`: it takes no generation of its own and is
   * cancelled by a project change alone. Otherwise a second operation
   * starting while the delete is in flight (reopening the panel refetches)
   * would discard the delete's own removal and leave a row the server has
   * already deleted -- clickable, and 404ing on click.
   *
   * `ownsSpinner()` stays generation-scoped for all three: whoever is still
   * loading owns `isLoading`, so a resolved delete cannot retire a spinner
   * that belongs to a list load started after it.
   */
  const beginRequest = ({ supersedesLoads = true } = {}) => {
    const generation = supersedesLoads
      ? ++requestGenerationRef.current
      : requestGenerationRef.current;
    const projectId = selectedProjectId;
    const isSameProject = () => currentProjectRef.current === projectId;
    const isLatest = () => requestGenerationRef.current === generation;
    return {
      isCurrent: () => isSameProject() && (!supersedesLoads || isLatest()),
      ownsSpinner: () => isSameProject() && isLatest(),
    };
  };

  // Fetch fragments from API
  const refetchFragments = async () => {
    if (!selectedProjectId) {
      setFragments([]);
      return;
    }

    const request = beginRequest();
    setIsLoading(true);
    setError(null);

    try {
      const response = await listFragments(selectedProjectId);
      if (!request.isCurrent()) return;
      setFragments(response.fragments);
      setHasLoadedFragments(true);
    } catch (err) {
      if (!request.isCurrent()) return;
      setError(getApiErrorMessage(err, 'Failed to fetch fragments'));
      console.error('Error fetching fragments:', err);
    } finally {
      if (request.ownsSpinner()) setIsLoading(false);
    }
  };

  // Clear fragments when project changes (don't auto-fetch)
  useEffect(() => {
    setFragments([]);
    setHasLoadedFragments(false);
    setSearchTerm(''); // Reset search when project changes
    // Any request still in flight belongs to the project being left and will
    // no longer commit anything, its own `finally` included. Retiring the
    // spinner and the banner here is what keeps them from outliving it.
    setIsLoading(false);
    setError(null);
  }, [selectedProjectId]);

  // Define is an upsert: create and edit both land here. Errors re-throw so
  // the editor modal can surface the server's 400 detail inline.
  const saveFragment = async (body: DefineFragmentRequest) => {
    if (!selectedProjectId) {
      throw new Error('No project selected');
    }

    const request = beginRequest();
    setIsLoading(true);
    setError(null);

    // No catch: errors propagate to the editor modal (which shows the
    // server's 400 detail inline) instead of the panel error banner.
    try {
      await defineFragment(selectedProjectId, body);
      // Refresh the list so params/doc summaries reflect the server's view.
      const response = await listFragments(selectedProjectId);
      if (!request.isCurrent()) return;
      setFragments(response.fragments);
      setHasLoadedFragments(true);
    } finally {
      if (request.ownsSpinner()) setIsLoading(false);
    }
  };

  const removeFragment = async (name: string) => {
    if (!selectedProjectId) {
      throw new Error('No project selected');
    }

    // Removes one row rather than replacing the list, so it is cancelled by
    // a project change alone -- see `beginRequest`.
    const request = beginRequest({ supersedesLoads: false });
    setIsLoading(true);
    setError(null);

    // The failure is re-thrown for the caller's toast and deliberately does
    // NOT set `error`: that flag drives the panel's list-load error banner,
    // which replaces the whole list and has no way to be cleared. One failed
    // delete would otherwise cost the reader the list until the panel is
    // closed and reopened, on top of showing the same message twice.
    try {
      await deleteFragment(selectedProjectId, name);
      if (!request.isCurrent()) return;
      setFragments((prev) =>
        prev.filter((fragment) => fragment.name !== name)
      );
    } finally {
      if (request.ownsSpinner()) setIsLoading(false);
    }
  };

  const fetchFragmentDetail = async (
    name: string
  ): Promise<FragmentDetail> => {
    if (!selectedProjectId) {
      throw new Error('No project selected');
    }
    return getFragment(selectedProjectId, name);
  };

  // The list endpoint has no search param, so filter client-side on
  // name / doc / param names, plus a governed row's question and runtime
  // param names.
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredFragments = normalizedSearch
    ? fragments.filter(
        (fragment) =>
          fragment.name.toLowerCase().includes(normalizedSearch) ||
          fragment.doc.toLowerCase().includes(normalizedSearch) ||
          fragment.params.some((param) =>
            param.toLowerCase().includes(normalizedSearch)
          ) ||
          (fragment.type === PIPELINE_TYPE.GOVERNED &&
            (fragment.question.toLowerCase().includes(normalizedSearch) ||
              fragment.runtime_params.some((param) =>
                param.name.toLowerCase().includes(normalizedSearch)
              )))
      )
    : fragments;

  const value: FragmentsContextType = {
    fragments,
    isEditorModalOpen,
    setIsEditorModalOpen,
    currentEditingFragment,
    setCurrentEditingFragment,
    searchTerm,
    setSearchTerm,
    isLoading,
    error,
    hasLoadedFragments,
    saveFragment,
    removeFragment,
    fetchFragmentDetail,
    filteredFragments,
    refetchFragments,
  };

  return (
    <FragmentsContext.Provider value={value}>
      {children}
    </FragmentsContext.Provider>
  );
};

export const useFragments = (): FragmentsContextType => {
  const context = useContext(FragmentsContext);
  if (context === undefined) {
    throw new Error('useFragments must be used within a FragmentsProvider');
  }
  return context;
};
